import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AgentType } from '../../models/agent';
import { IRoleReadModel } from '../../models/role';
import { IConnectedUserModel } from '../../models/user';
import { getAgentType } from '../../utils/agent.helper';
import { useGetMyRoleQuery } from '../../redux/apis/roleApi';
import { useGetTenantByIdQuery } from '../../redux/apis/tenantsApi';
import { useGetConnectedUsersQuery, useGetUserByIdQuery } from '../../redux/apis/userApi';
import { selectTenantId, selectUserId } from '../../app/authentication/authSelectors';

interface ITenantInfo {
    id: string;
    name: string;
    environment?: string;
    scopes: string[];
}

interface IUserInfo {
    id?: string;
    name?: string;
    email?: string;
    type?: 'user' | AgentType;
    isUserSupervisor: boolean;
    tenant?: ITenantInfo;
    role?: IRoleReadModel;
    connectedAccounts?: IConnectedUserModel[];
}

function useUserInfo(): IUserInfo {
    const tenantId = useSelector(selectTenantId);
    const userId = useSelector(selectUserId);
  
    const isInitialized = !!tenantId && !!userId;

    const { data: user } = useGetUserByIdQuery(userId ?? '', { skip: !isInitialized });
    const { data: tenant } = useGetTenantByIdQuery(tenantId ?? '', { skip: !isInitialized });
    const { data: role } = useGetMyRoleQuery(undefined, { skip: !isInitialized });
    const { data: accounts } = useGetConnectedUsersQuery(userId ?? '', { skip: !isInitialized });
    
    const info = useMemo(
        () => ({
            id: user?.id,
            name: user?.name,
            email: user?.emailAddress,
            isUserSupervisor: user ? getAgentType(user) === 'supervisor' : false,
            tenant: tenant
                ? {
                    id: tenant.id,
                    name: tenant.name,
                    environment: tenant.environment,
                    scopes: tenant.scopes.map(s => s.name),
                }
                : undefined,
            role,
            connectedAccounts: accounts,
        }),
        [accounts,role, tenant, user],
    );

    return info;
}

export default useUserInfo;
